import app from 'app';
import moment from 'moment';
import { coerce as semverCoerce, neq as semverNeq } from 'semver';
import { mapState } from 'vuex';

class WochitStoryContainer {
  constructor({ story = {}, metadata = {}, json = {} }) {
    if (_.isEmpty(json)) {
      this.header = {
        version: WochitStoryContainer.version,
        mimeType: 'wochit/story',
        metadata: metadata,
      };
      this.data = story;
    } else if (json && json.header.mimeType === 'wochit/story') {
      this.header = json.header;
      this.data = json.data;
    }
  }

  static get version() {
    return '2.0.0';
  }
}

app.importExportController = new (class ImportExportController {
  /**
   * export current story to json file
   */
  export() {
    if (!app.storyController.story) {
      alert('Start story you want to export...');
      return;
    }

    const story = app.storyController.story.serialize();
    const wsc = new WochitStoryContainer({
      story: story,
      metadata: {
        userId: app.rootStore.getters['user/id'],
        timestamp: Date.now(),
        profile: serverProfile.environment,
      },
    });

    const jsonString = JSON.stringify(wsc, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const filename = `wochit-${serverProfile.environment}.${
      story.id
    }.${moment().format('YYYY-MM-DDTHH-mm-ss')}.json`;
    const file = new File([blob], filename);
    this._saveFile(file, filename).then((name) => {
      console.info('DONE', name);
    });
  }

  _saveFile(file, name) {
    const rv = new _.ColdPromise();

    const el = document.createElement('A');
    el.setAttribute('id', `saveFile-${Date.now()}`);
    el.setAttribute('download', name);
    el.setAttribute('target', '_blank');
    const uri = URL.createObjectURL(file);
    el.setAttribute('href', uri);

    document.body.appendChild(el);
    el.click();
    document.body.removeChild(el);
    URL.revokeObjectURL(uri);

    rv.resolve(name);
    return rv;
  }

  static get importStoryDialog() {
    return {
      name: 'import-story-dialog',
      store: app.rootStore,

      // language=Vue
      template: `
      <div class="wt-import-export-dialog">
        <div class="-head wt2-text-l2">Import Story from file</div>
        <div class="-body">
          <div v-if="singleProgrammer"
               class="-programmer">
            <span class="wt2-color-black">{{'rebrand.productionLine' | culture}}</span>
            <wt-programmers-dropdown
                type="underline"
                :organization-id="selectedOrganizationId"
                :current-programmer-id.sync="selectedProgrammerId"
            />
          </div>
          <wt-file
              accept="application/json"
              :placeholder="(file? file.name : 'Browse for wochit-*.json')"
              @change="onChangeFile">
            <!-- slot favicon can replace existing favicon button -->
          </wt-file>
        </div>
        <div class="-footer">
          <wt-button type="primary" text="Import" @click="done"/>
        </div>
      </div>
    `,
      props: {
        closeModal: { type: Function },
      },
      computed: {
        ...mapState({
          selectedOrganizationId: (state) => {
            return state.user.selectedOrganizationId;
          },
        }),

        singleProgrammer() {
          return app.programmers.length > 1;
        },
      },
      data() {
        return {
          selectedProgrammerId: app.programmers.first().id,
          file: null,
        };
      },
      methods: {
        onChangeFile(e) {
          this.file = e.target.files && e.target.files[0];
        },
        done() {
          this.closeModal({
            programmerId: this.selectedProgrammerId,
            file: this.file,
          });
        },
      },
    };
  }

  /**
   * execute from dashboard to create story from external json file
   * @note: not all properties are copied from external file (see in code)
   * @note: currently narration should be re-uploaded if VO story migrating between environments
   */
  import() {
    const pledgeStorySelected = new _.ColdPromise();
    const pledgeStoryStarted = new _.ColdPromise();
    const pledgeStoryFinished = new _.ColdPromise();

    app
      .showModal({
        type: undefined,
        component: ImportExportController.importStoryDialog,
        componentPropsData: {},
        wrapperClass: ['wt-import-export-modal'],
      })
      .$on('close', (resDialog) => {
        if (!resDialog.programmerId || !resDialog.file) {
          return;
        }

        this._selectJsonFile(resDialog.file).then((resFile) => {
          const wsc = new WochitStoryContainer({ json: resFile.parsed });
          const fileVersion = wsc.header?.version;

          if (
            semverNeq(
              semverCoerce(fileVersion),
              semverCoerce(WochitStoryContainer.version)
            )
          ) {
            alert(
              `Current version ${WochitStoryContainer.version} doesn't supports file version ${fileVersion}`
            );
            return;
          }

          pledgeStorySelected.resolve({
            wsc: wsc,
            filename: resFile.filename,
            programmerId: resDialog.programmerId,
          });
        });
      });

    pledgeStorySelected.then(({ wsc, filename, programmerId }) => {
      app.delayedSpinner.show({ msg: `Importing story file: "${filename}"` });
      app.api.draftStories
        .createNew({
          programmerId,
          storyStartPoint: wsc.data.storyStartPoint,
        })
        .done((res) => {
          app.api.draftStories.getStory(res.id).done(({ story }) => {
            pledgeStoryStarted.resolve({ story, wsc, filename });
          });
        });
    });

    pledgeStoryStarted.then(({ story, wsc, filename }) => {
      const fs = wsc.data;
      const fsProfile = wsc.header.metadata.profile;
      const sameProfile = serverProfile.environment === fsProfile;
      const pickupProperties = [
        'aspectRatio',
        'applyAutoTransition',
        'enableVOSubtitles',
        'enableVOWaveform',
        'attributedBy',
        'storyUrl',
        'topicId',
        'seoTags',
        'type',
        'youtubeChannelUID',
        'description',
        'videoPosterAsset',
        'selectedAssets',
        'segments',
        'audioTracks',
      ];
      if (sameProfile) {
        // otherwise - causes NPE while producing video
        pickupProperties.push('chosenBGMusic');
      }

      Object.assign(story, _.pick(fs, pickupProperties), {
        title: `[imported] ${fs.title}`,
        description: `Imported from file: "${filename}"\n${
          fs.description || ''
        }`,
      });

      story.visualTimeline = {};
      const storyTimeline = story.visualTimeline;
      const fsTimeline = fs.visualTimeline;
      Object.assign(
        storyTimeline,
        _.pick(fsTimeline, ['visualTracks', 'totalDisplayTime'])
      );

      if (!sameProfile) {
        // reset RTP content so it will be rendered with new CDN URLs
        _.each(storyTimeline.visualTracks, (visualTrack) => {
          visualTrack.realTimePreviewResultData = null;
        });
      }

      if (fs.type === 'VO') {
        const hasAudio = _.some(
          fs.segments,
          (s) => s.audioSegmentContent && s.audioSegmentContent.asset
        );
        if (hasAudio && !sameProfile) {
          alert(
            `Importing from "${fsProfile}" profile.\nSome things may break 🤷‍`
          );
        }

        pledgeStoryFinished.resolve(story);
      } else {
        pledgeStoryFinished.resolve(story);
      }
    });

    pledgeStoryFinished.then((story) => {
      app.api.draftStories.manualSave(story).done(() => {
        app.navigate(`stories/${story.id}`);
      });
    });
  }

  _importNarrations({ story, fs }) {
    const rv = new _.ColdPromise();
    const aPledgeAllDone = [];

    for (let i = 0, I = fs.segments.length; i < I; i++) {
      const segment = fs.segments[i];
      const pledgeSegmentUpload = new _.ColdPromise();
      aPledgeAllDone.push(pledgeSegmentUpload);

      this._getUrlAsFile(
        segment.audioSegmentContent.asset.metadata.hiResAssetUrl
      ).then((file) => {
        this._uploadNarrationFile({ file, programmerId: story.programmerId })
          .then((asc) => {
            story.segments[i].audioSegmentContent = asc;
            pledgeSegmentUpload.resolve();
          })
          .catch(pledgeSegmentUpload.reject);
      });
    }

    Promise.all(aPledgeAllDone)
      .then(() => {
        rv.resolve(story);
      })
      .catch(rv.reject);

    return rv;
  }

  _selectJsonFile(file) {
    const rv = new _.ColdPromise();
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const json = e.target.result;
        rv.resolve({
          parsed: json ? JSON.parse(json) : {},
          filename: file.name,
        });
      };
      reader.readAsText(file);
    } else {
      rv.reject();
    }
    return rv;
  }

  _uploadNarrationFile({ file, programmerId }) {
    const rv = new _.ColdPromise();
    let path;

    rv.finally(() => {
      path && app.api.realTime.cancelListen(path);
    });
    app.api.narration
      .post(programmerId, file)
      .done((res) => {
        path = `/users/${app.rootStore.getters['user/id']}/narrations/${res.uuid}`;

        const timeout = setTimeout(() => {
          rv.reject();
        }, app.TIME.minute(5));

        app.api.realTime.listenOn({
          path,
          callback: ({ val }) => {
            const message = val && val.message;

            if (message === 'DONE') {
              clearTimeout(timeout);
              app.api.narration.get(res.uuid).done((asset) => {
                rv.resolve(asset);
              });
            } else if (message && /^FAIL/i.test(message)) {
              clearTimeout(timeout);
              rv.reject();
            }
          },
          errorCallback: rv.reject,
        });
      })
      .fail(rv.reject);

    return rv;
  }

  _getUrlAsFile(url) {
    const rv = new _.ColdPromise();

    this._urlToBlob(app.prependHttp(url))
      .then((blob) => {
        const file = new File([blob], `${app.guid('narration')}.wav`, {
          type: 'audio/wav',
        });
        rv.resolve(file);
      })
      .catch(rv.reject);

    return rv;
  }

  _urlToBlob(url) {
    const rv = new _.ColdPromise();
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';
    xhr.onerror = function () {
      rv.reject('error');
    };
    xhr.onload = function () {
      rv.resolve(xhr.response);
    };
    xhr.send(null);
    return rv;
  }
})();
